import { UserRole } from "../../models/enums";
import { appConfig } from "../../services/configService";

export interface layerOptionsType {
  conductingSections: {
    id: string;
    text: string;
    value: string;
    visible: (userRole?: UserRole) => boolean;
    ragLayer: boolean;
    geoServerAddress: string[];
    geoServerType: EPSGType;
  };
  overheadPoles: {
    id: string;
    text: string;
    value: string;
    visible: (userRole?: UserRole) => boolean;
    ragLayer: boolean;
    geoServerAddress: string[];
    geoServerType: EPSGType;
  };
  [layerOption: string]: {
    id: string;
    text: string;
    value: string;
    visible: (userRole?: UserRole) => boolean;
    ragLayer: boolean;
    geoServerAddress: string[] | undefined;
    geoServerType: EPSGType;
  };
}

export enum layerOptionValues {
  conductingSections = "conductingSections",
  powerTransformers = "powerTransformers",
  overheadPoles = "overheadPoles",
  lvServices = "serviceCables",
  lvMains = "lvmains",
  hvs = "hvs",
  ehvs = "ehvs",
  thirdParties = "thirdparties",
  wayleaves = "wayleaves",
  mainsLabels = "mains-cable-type",
  acceptedToConnect = "acceptedToConnect",
}

export enum EPSGType {
  latLng = "EPSG:4326",
  eastingNorthing = "EPSG:27700",
}

// Maps layer availability to a user role, allowing granular switching of layer visibility
const layerRoleMappings: Record<UserRole, Record<layerOptionValues, boolean>> = {
  [UserRole.developer]: {
    [layerOptionValues.conductingSections]: true,
    [layerOptionValues.powerTransformers]: true,
    [layerOptionValues.overheadPoles]: true,
    [layerOptionValues.lvServices]: true,
    [layerOptionValues.lvMains]: true,
    [layerOptionValues.hvs]: true,
    [layerOptionValues.ehvs]: true,
    [layerOptionValues.thirdParties]: true,
    [layerOptionValues.wayleaves]: true,
    [layerOptionValues.mainsLabels]: true,
    [layerOptionValues.acceptedToConnect]: true,
  },
  [UserRole.icp]: {
    [layerOptionValues.conductingSections]: true,
    [layerOptionValues.powerTransformers]: true,
    [layerOptionValues.overheadPoles]: true,
    [layerOptionValues.lvServices]: true,
    [layerOptionValues.lvMains]: true,
    [layerOptionValues.hvs]: true,
    [layerOptionValues.ehvs]: true,
    [layerOptionValues.thirdParties]: true,
    [layerOptionValues.wayleaves]: true,
    [layerOptionValues.mainsLabels]: true,
    [layerOptionValues.acceptedToConnect]: false,
  },
  [UserRole.nged]: {
    [layerOptionValues.conductingSections]: true,
    [layerOptionValues.powerTransformers]: true,
    [layerOptionValues.overheadPoles]: true,
    [layerOptionValues.lvServices]: true,
    [layerOptionValues.lvMains]: true,
    [layerOptionValues.hvs]: true,
    [layerOptionValues.ehvs]: true,
    [layerOptionValues.thirdParties]: true,
    [layerOptionValues.wayleaves]: true,
    [layerOptionValues.mainsLabels]: true,
    [layerOptionValues.acceptedToConnect]: true,
  },
  [UserRole.public]: {
    [layerOptionValues.conductingSections]: true,
    [layerOptionValues.powerTransformers]: true,
    [layerOptionValues.overheadPoles]: true,
    [layerOptionValues.lvServices]: true,
    [layerOptionValues.lvMains]: true,
    [layerOptionValues.hvs]: true,
    [layerOptionValues.ehvs]: true,
    [layerOptionValues.thirdParties]: true,
    [layerOptionValues.wayleaves]: true,
    [layerOptionValues.mainsLabels]: true,
    [layerOptionValues.acceptedToConnect]: true,
  },
};

const { visibleLayers } = appConfig;

/**
 * Checks if a layer is visible to the user
 * @param {layerOptionValues} layer The layer to check
 * @param {UserRole} userRole The role of the user
 * @returns {boolean} Whether the layer is visible
 */
const isLayerVisible = (layer: layerOptionValues, userRole?: UserRole): boolean => {
  if (userRole && !layerRoleMappings[userRole][layer]) {
    return false;
  }

  return visibleLayers.includes(layer);
};

/**
 *
 * @param {UserRole} userRole The role the current user has
 * @returns {Object[]} An array of objects containing the layer id and whether it is a RAG layer
 */
export const getAllVisibleLayers = (userRole?: UserRole): { id: string; ragLayer: boolean }[] =>
  Object.values(layerOptionValues)
    .filter((layer) => isLayerVisible(layer, userRole))
    .map((layer) => layerOptions[layer]);

export const layerOptions: layerOptionsType = {
  conductingSections: {
    id: "1",
    text: "Conducting Sections",
    value: layerOptionValues.conductingSections,
    visible: (role) => isLayerVisible(layerOptionValues.conductingSections, role),
    ragLayer: true,
    geoServerAddress: ["AcLineSegment"],
    geoServerType: EPSGType.latLng,
  },
  powerTransformers: {
    id: "1",
    text: "Power Transformers",
    value: layerOptionValues.powerTransformers,
    visible: (role) => isLayerVisible(layerOptionValues.powerTransformers, role),
    ragLayer: true,
    geoServerAddress: ["PowerTransformer"],
    geoServerType: EPSGType.latLng,
  },
  overheadPoles: {
    id: "2",
    text: "Overhead Poles",
    value: layerOptionValues.overheadPoles,
    visible: (role) => isLayerVisible(layerOptionValues.overheadPoles, role),
    ragLayer: true,
    geoServerAddress: ["JunctionIsPole"],
    geoServerType: EPSGType.latLng,
  },
  lv: {
    id: "3",
    text: "LV Services",
    value: layerOptionValues.lvServices,
    visible: (role) => isLayerVisible(layerOptionValues.lvServices, role),
    ragLayer: false,
    geoServerAddress: [
      "lvservice",
      "connectionpoint",
      "customergeneration",
      "duct",
      "feederpillar",
      "lvjoint",
      "lvtermination",
      "meteredservicetermination",
      "publiclightingpillar",
      "servicedistributionunit",
      "streetfurniture",
      "ufs_duct",
      "ufs_lvservice",
      "unmeteredconnectionpoint",
      "unmeteredservicetermination",
      "wallbox",
    ],
    geoServerType: EPSGType.latLng,
  },
  hv: {
    id: "4",
    text: "HV",
    value: layerOptionValues.hvs,
    visible: (role) => isLayerVisible(layerOptionValues.hvs, role),
    ragLayer: false,
    geoServerAddress: ["hv", "supportstay", "transformer", "duct", "ufs_duct", "ufs_hv"],
    geoServerType: EPSGType.latLng,
  },
  ehv: {
    id: "5",
    text: "EHV",
    value: layerOptionValues.ehvs,
    visible: (role) => isLayerVisible(layerOptionValues.ehvs, role),
    ragLayer: false,
    geoServerAddress: ["ehv", "ufs_ehv"],
    geoServerType: EPSGType.latLng,
  },
  thirdparties: {
    id: "6",
    text: "Third Party",
    value: layerOptionValues.thirdParties,
    visible: (role) => isLayerVisible(layerOptionValues.thirdParties, role),
    ragLayer: false,
    geoServerAddress: ["idnoboundary"],
    geoServerType: EPSGType.latLng,
  },
  lvmains: {
    id: "7",
    text: "LV Mains",
    value: layerOptionValues.lvMains,
    visible: (role) => isLayerVisible(layerOptionValues.lvMains, role),
    ragLayer: false,
    geoServerAddress: [
      "duct",
      "lvmain",
      "lvjoint",
      "linkbox",
      "lvtermination",
      "meteredservicetermination",
      "site",
      "supportlocation",
      "ufs_duct",
      "ufs_lvmain",
    ],
    geoServerType: EPSGType.latLng,
  },
  wayleaves: {
    id: "8",
    text: "Wayleaves",
    value: layerOptionValues.wayleaves,
    visible: (role) => isLayerVisible(layerOptionValues.wayleaves, role),
    ragLayer: false,
    geoServerAddress: ["wayleave"],
    geoServerType: EPSGType.latLng,
  },
  mainsLabels: {
    id: "9",
    text: "Mains Cable Type",
    value: layerOptionValues.mainsLabels,
    visible: (role) => isLayerVisible(layerOptionValues.mainsLabels, role),
    ragLayer: false,
    geoServerAddress: undefined,
    geoServerType: EPSGType.latLng,
  },
  acceptedToConnect: {
    id: "10",
    text: "Accepted To Connect",
    value: layerOptionValues.acceptedToConnect,
    visible: (role) => isLayerVisible(layerOptionValues.acceptedToConnect, role),
    ragLayer: false,
    geoServerAddress: ["AcceptedToConnectLV"],
    geoServerType: EPSGType.eastingNorthing,
  },
};
